/**
 * teleportToBody — Vue 2 mixin that moves an element to document.body
 * once it's rendered. Use it on modal child components whose .ph-modal-
 * backdrop element gets trapped by an ancestor that has any `transform`
 * applied (e.g. PricingPage's [data-reveal] sections that animate in
 * with translateY).
 *
 * Browsers create a "containing block" for position:fixed children when
 * any ancestor has a transform other than `none` — this includes
 * `transform: translateY(0)` left behind after a reveal animation
 * completes. The fixed-positioned modal anchors to that ancestor's
 * box instead of the viewport, so it appears tiny / off-screen.
 *
 * Mixin contract:
 *   - Component must declare `teleportRef: 'someRefName'` in its data()
 *     OR override `getTeleportEl()` to return the element to move.
 *   - Default looks for `this.$refs.backdrop` first, then any element
 *     with class `.ph-modal-backdrop` inside this.$el.
 *   - Component must declare a boolean prop or data named `modalOpen`
 *     OR `open` so the mixin knows when to teleport.
 *   - On open → move element to document.body.
 *   - On close (or destroy) → if the element is still attached to body
 *     and not already detached, remove it (Vue can re-render later).
 */
export default {
  data() {
    return {
      teleportTargetEl: null,
    };
  },

  watch: {
    modalOpen(val) { this._handleTeleport(val); },
    open(val) { this._handleTeleport(val); },
  },

  methods: {
    _findBackdrop() {
      if (this.$refs && this.$refs.backdrop) return this.$refs.backdrop;
      if (this.$el && this.$el.querySelector) {
        return this.$el.querySelector('.ph-modal-backdrop');
      }
      return null;
    },

    _handleTeleport(isOpen) {
      this.$nextTick(() => {
        if (isOpen) {
          // Modal just rendered — move backdrop to body.
          const el = this._findBackdrop();
          if (el && el.parentNode !== document.body) {
            this.teleportTargetEl = el;
            document.body.appendChild(el);
          }
        } else {
          // Modal closed — Vue's v-if will unmount the node, but if it
          // got teleported it's no longer Vue's child. Manually remove.
          if (this.teleportTargetEl && this.teleportTargetEl.parentNode === document.body) {
            this.teleportTargetEl.remove();
            this.teleportTargetEl = null;
          }
        }
      });
    },
  },

  beforeDestroy() {
    // Defence in depth — if the component is destroyed while the modal
    // is open, scrub the orphan node from body.
    if (this.teleportTargetEl && this.teleportTargetEl.parentNode === document.body) {
      this.teleportTargetEl.remove();
      this.teleportTargetEl = null;
    }
  },
};
